import { create } from 'zustand'

import type { FieldValue } from '@/lib/fields/types'

import type { ModuleTypeEnum } from '@scrambl/shared'

const MAX_TRANSACTIONS = 100
const MAX_OPERATIONS = 500

export interface FieldHistoryOperation {
  id: string
  moduleType: ModuleTypeEnum
  moduleId: string
  fieldId: string
  fieldName: string
  previousValue: FieldValue
  nextValue: FieldValue
  label?: string
  context?: {
    collectionId?: string
  }
}

export interface FieldHistoryTransaction {
  id: string
  label?: string
  createdAt: number
  operations: FieldHistoryOperation[]
}

type ReplayDirection = 'undo' | 'redo'

interface FieldHistoryState {
  undoStack: FieldHistoryTransaction[]
  redoStack: FieldHistoryTransaction[]
  isReplaying: boolean
  canUndo: () => boolean
  canRedo: () => boolean
  record: (operation: FieldHistoryOperation, transactionId?: string) => void
  undo: (
    executor: (
      transaction: FieldHistoryTransaction,
      direction: ReplayDirection,
    ) => Promise<void>,
  ) => Promise<boolean>
  redo: (
    executor: (
      transaction: FieldHistoryTransaction,
      direction: ReplayDirection,
    ) => Promise<void>,
  ) => Promise<boolean>
  clear: () => void
}

const countOperations = (transactions: FieldHistoryTransaction[]) =>
  transactions.reduce((total, transaction) => total + transaction.operations.length, 0)

const trimTransactions = (
  transactions: FieldHistoryTransaction[],
): FieldHistoryTransaction[] => {
  let next = transactions.slice(-MAX_TRANSACTIONS)

  while (countOperations(next) > MAX_OPERATIONS && next.length > 1) {
    next = next.slice(1)
  }

  return next
}

const createTransaction = (
  operation: FieldHistoryOperation,
  transactionId?: string,
): FieldHistoryTransaction => ({
  id: transactionId || operation.id,
  label: operation.label,
  createdAt: Date.now(),
  operations: [operation],
})

export const useFieldHistoryStore = create<FieldHistoryState>((set, get) => ({
  undoStack: [],
  redoStack: [],
  isReplaying: false,

  canUndo: () => get().undoStack.length > 0 && !get().isReplaying,
  canRedo: () => get().redoStack.length > 0 && !get().isReplaying,

  record: (operation, transactionId) => {
    if (get().isReplaying) return

    set(state => {
      const undoStack = [...state.undoStack]
      const last = undoStack.at(-1)

      if (transactionId && last?.id === transactionId) {
        undoStack[undoStack.length - 1] = {
          ...last,
          operations: [...last.operations, operation],
        }
      } else {
        undoStack.push(createTransaction(operation, transactionId))
      }

      return {
        undoStack: trimTransactions(undoStack),
        redoStack: [],
      }
    })
  },

  undo: async executor => {
    const transaction = get().undoStack.at(-1)
    if (!transaction || get().isReplaying) return false

    set({ isReplaying: true })
    try {
      await executor(transaction, 'undo')
      set(state => ({
        undoStack: state.undoStack.slice(0, -1),
        redoStack: trimTransactions([...state.redoStack, transaction]),
      }))
      return true
    } finally {
      set({ isReplaying: false })
    }
  },

  redo: async executor => {
    const transaction = get().redoStack.at(-1)
    if (!transaction || get().isReplaying) return false

    set({ isReplaying: true })
    try {
      await executor(transaction, 'redo')
      set(state => ({
        redoStack: state.redoStack.slice(0, -1),
        undoStack: trimTransactions([...state.undoStack, transaction]),
      }))
      return true
    } finally {
      set({ isReplaying: false })
    }
  },

  clear: () => {
    set({ undoStack: [], redoStack: [], isReplaying: false })
  },
}))
